/**
 * Standalone language detection for TTS and card repair.
 * NO imports from gemini, flashcardAutomation, or any browser-only modules.
 * Safe for Cloudflare Functions / serverless bundles.
 */
import type { DeckLang } from "@/types/linguadeck";

const LINE_BREAK_REGEX = /\r\n?/g;
type LanguageScoreMap = Record<DeckLang, number>;

const LATIN_CODES: DeckLang[] = ["en", "es", "fr", "de", "it", "nl", "pl", "pt-BR", "tr"];
const EMPTY_SCORES: LanguageScoreMap = {
  en: 0, es: 0, fr: 0, de: 0, it: 0, nl: 0, pl: 0, "pt-BR": 0, tr: 0, ko: 0, ru: 0, uk: 0,
};

const LATIN_LANGUAGE_HINTS: Record<
  (typeof LATIN_CODES)[number],
  { keywords: string[]; fragments: string[]; chars: string[] }
> = {
  en: { keywords: ["the", "and", "with", "for", "travel", "weather", "school", "happy", "sad", "city", "country", "book", "ticket", "airport"], fragments: ["th", "ing", "tion", "ness", "ship", "ly", "ow", "ea"], chars: [] },
  es: { keywords: ["el", "la", "los", "las", "un", "una", "gracias", "hola", "viaje", "tiempo", "ciudad", "escuela"], fragments: ["ción", "que", "ll", "ado", "ada", "mente", "está"], chars: ["á", "é", "í", "ó", "ú", "ñ", "¿", "¡"] },
  fr: { keywords: ["le", "la", "les", "un", "une", "des", "bonjour", "merci", "voyage", "météo", "ville", "école"], fragments: ["eau", "eux", "ait", "ette", "tion", "ique"], chars: ["à", "â", "ç", "é", "è", "ê", "ë", "î", "ï", "ô", "ù", "û", "ü", "ÿ", "œ"] },
  de: { keywords: ["der", "die", "das", "ein", "eine", "hallo", "danke", "reise", "wetter", "stadt", "schule", "flughafen"], fragments: ["sch", "ung", "keit", "heit", "chen", "lich", "tz", "ei", "ie"], chars: ["ä", "ö", "ü", "ß"] },
  it: { keywords: ["il", "lo", "la", "gli", "una", "uno", "ciao", "grazie", "viaggio", "tempo", "città", "scuola"], fragments: ["zione", "gli", "che", "mente", "tta", "zione"], chars: ["à", "è", "é", "ì", "ò", "ù"] },
  nl: { keywords: ["de", "het", "een", "hallo", "dank", "reis", "weer", "stad", "school"], fragments: ["ij", "sch", "lijk", "heid", "een"], chars: [] },
  pl: { keywords: ["jest", "nie", "dziękuję", "cześć", "podróż", "pogoda", "miasto", "szkoła"], fragments: ["sz", "cz", "rz", "owa", "anie", "ować"], chars: ["ą", "ć", "ę", "ł", "ń", "ó", "ś", "ź", "ż"] },
  "pt-BR": { keywords: ["o", "a", "os", "as", "um", "uma", "obrigado", "olá", "viagem", "tempo", "cidade", "escola"], fragments: ["ção", "ções", "nh", "lh", "dade", "mente"], chars: ["á", "â", "ã", "à", "ç", "é", "ê", "í", "ó", "ô", "õ", "ú"] },
  tr: { keywords: ["bir", "ve", "çok", "merhaba", "teşekkürler", "seyahat", "hava", "şehir", "okul"], fragments: ["iyor", "mak", "mek", "lar", "ler", "lik"], chars: ["ç", "ğ", "ı", "İ", "ö", "ş", "ü"] },
};

function normalizeInput(text: string): string {
  return text
    .replace(LINE_BREAK_REGEX, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "")
    .trim();
}

function containsHangul(text: string): boolean {
  return /[\uac00-\ud7af]/u.test(text);
}

function containsCyrillic(text: string): boolean {
  return /[\u0400-\u04ff]/u.test(text);
}

export function containsLatin(text: string): boolean {
  return /[A-Za-zÀ-ÿ]/u.test(text);
}

function scoreCyrillic(text: string): { code: DeckLang | null; score: number } {
  const normalized = text.toLowerCase();
  const tokens = normalized.match(/\p{L}+/gu) ?? [];
  const ukKeywords = new Set(["це", "та", "і", "привіт", "дякую", "подорож", "погода", "місто", "школа", "сонячно", "хмарно", "дощ"]);
  const ruKeywords = new Set(["это", "и", "привет", "спасибо", "путешествие", "погода", "город", "школа", "солнечно", "облачно", "дождь"]);
  const ukScore =
    (normalized.match(/[іїєґ]/gu)?.length ?? 0) * 4 +
    tokens.filter((token) => ukKeywords.has(token)).length * 3;
  const ruScore =
    (normalized.match(/[ёъыэ]/gu)?.length ?? 0) * 4 +
    tokens.filter((token) => ruKeywords.has(token)).length * 3;
  if (ukScore === 0 && ruScore === 0) return { code: "ru", score: 1 };
  return ukScore >= ruScore ? { code: "uk", score: ukScore } : { code: "ru", score: ruScore };
}

function scoreLatin(text: string): { code: DeckLang | null; score: number; scores: LanguageScoreMap } {
  const normalized = text.toLowerCase();
  const tokens = normalized.match(/\p{L}+/gu) ?? [];
  const scores = { ...EMPTY_SCORES };
  for (const code of LATIN_CODES) {
    const hint = LATIN_LANGUAGE_HINTS[code];
    for (const char of hint.chars) {
      scores[code] += (normalized.split(char).length - 1) * 4;
    }
    for (const keyword of hint.keywords) {
      scores[code] += tokens.filter((token) => token === keyword).length * 3;
    }
    for (const fragment of hint.fragments) {
      scores[code] += (normalized.match(new RegExp(fragment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gu"))?.length ?? 0);
    }
  }
  if (containsLatin(normalized)) scores.en += tokens.length > 0 ? 1 : 0;
  const ranked = Object.entries(scores)
    .filter(([code]) => LATIN_CODES.includes(code as DeckLang))
    .sort((a, b) => b[1] - a[1]);
  const [topCode, topScore] = ranked[0] as [DeckLang, number];
  const secondScore = ranked[1]?.[1] ?? 0;
  if (topScore === 0) return { code: null, score: 0, scores };
  if (topScore === 1 && secondScore === 1) return { code: null, score: topScore, scores };
  if (topScore <= secondScore) return { code: null, score: topScore, scores };
  return { code: topCode, score: topScore, scores };
}

export interface DetectLanguageResult {
  code: DeckLang | null;
  score: number;
  scores: LanguageScoreMap;
}

export function detectLanguageWithScores(text: string): DetectLanguageResult {
  const normalized = normalizeInput(text);
  if (!normalized) return { code: null, score: 0, scores: { ...EMPTY_SCORES } };
  if (containsHangul(normalized)) {
    return { code: "ko", score: 20, scores: { ...EMPTY_SCORES, ko: 20 } };
  }
  if (containsCyrillic(normalized)) {
    const result = scoreCyrillic(normalized);
    return {
      code: result.code,
      score: result.score,
      scores: { ...EMPTY_SCORES, [result.code ?? "ru"]: result.score },
    };
  }
  return scoreLatin(normalized);
}

/** Single-text language detection for TTS / card language repair. Server-safe, no gemini. */
export function detectLanguageFromText(text: string): DeckLang | null {
  return detectLanguageWithScores(text).code;
}

/** Strong Spanish-only markers (not typical in Portuguese). */
const ES_ONLY = /ñ|ción|¿|¡/i;
/** Strong Portuguese-only markers (ã, õ, ção, ões). */
const PT_ONLY = /ã|õ|ção|ões|não|tempo|obrigado|olá/i;
/** Reinforced: keep pt-BR only if unmistakable Portuguese (word-boundary não, or ção/ões, or ã/õ in words). */
const PT_STRONG_REINFORCED = /(?:^|\s)n\u00e3o(?:\s|$)|ção|ões|[\u00e3\u00f5]/iu;

/**
 * Use when re-checking cards stored as Portuguese: prefer Spanish if ambiguous,
 * since Spanish is more common on the platform. Returns true if the text should be
 * treated as Spanish (e.g. for repair scripts).
 */
export function preferSpanishOverPortuguese(
  text: string,
  scores: LanguageScoreMap
): boolean {
  const t = text.trim();
  if (!t) return false;
  if (ES_ONLY.test(t)) return true;
  if (PT_ONLY.test(t)) return false;
  const esScore = scores.es ?? 0;
  const ptScore = scores["pt-BR"] ?? 0;
  if (esScore >= ptScore) return true;
  if (ptScore - esScore <= 3) return true; // ambiguous: prefer Spanish
  return false;
}

/**
 * Reinforced check: prefer Spanish unless there is clear Portuguese-only evidence.
 * Use for a second pass on pt-BR cards. Keeps pt-BR only if PT_STRONG_REINFORCED matches
 * (e.g. "não", "ção", "ões", or chars ã/õ); otherwise treats as Spanish.
 */
export function preferSpanishOverPortugueseReinforced(
  text: string,
  scores: LanguageScoreMap
): boolean {
  const t = text.trim();
  if (!t) return false;
  if (ES_ONLY.test(t)) return true;
  if (PT_STRONG_REINFORCED.test(t)) return false; // clear Portuguese: keep pt-BR
  const esScore = scores.es ?? 0;
  const ptScore = scores["pt-BR"] ?? 0;
  if (esScore >= ptScore) return true;
  if (ptScore - esScore <= 6) return true; // wider ambiguous band: prefer Spanish
  return false;
}
